/** tm/legal/LegalAlertSettingsTab.tsx — 설정 「알림」 탭: 아침 문자 요약·수신 번호·최소 건수·테스트 발송, 타법개정 알림, 뉴스 수집·제공처 상태 */
import { useEffect, useState, type ReactNode } from 'react';
import { useGuardReport, type SettingsGuard } from './settingsGuard';
import { C, T, R } from '../components/ui';
import { getSettings, saveSettings, testSms, getSummary, getNewsStatus } from './legalApi';
import { btn, inputStyle, labelStyle, errMsg, MUTED } from './legalTheme';
import { Spinner } from './legalUi';
import TermChips from './TermChips';
import PcNotifyToggle from './PcNotifyToggle';
import type { LegalSettings, TestSmsResult } from './legalTypes';

interface Props { onToast: (m: string, k?: 'ok' | 'error') => void; guard?: SettingsGuard }

const phoneErr = (v: string) => (/^01[016789]-?\d{3,4}-?\d{4}$/.test(v.replace(/\s/g, '')) ? null : '휴대폰 번호 형식이 아닙니다 (예: 010-1234-5678)');
/** 발송 오류 → 쉬운 말 (업체 이름·코드 대신) */
const smsErr = (e: string) => (/aligo|설정 없음|not configured|key/i.test(e) ? '문자 발송 서비스가 아직 연결되지 않았습니다 (관리자에게 문의)'
  : /잔액|포인트|balance/i.test(e) ? '문자 발송 잔액이 부족합니다 (관리자에게 문의)' : /번호|phone|receiver/i.test(e) ? '받는 번호를 확인해 주세요' : '보내지 못했습니다. 잠시 뒤 다시 시도해 주세요');
const fmtPhone = (v: string) => v.replace(/\D/g, '').replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');

function Toggle({ on, onChange, label, desc }: { on: boolean; onChange: (v: boolean) => void; label: string; desc?: ReactNode }) {
  return (
    <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}>
      <input type="checkbox" checked={on} onChange={e => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span><b style={{ fontSize: T.md, color: C.text, fontWeight: 600 }}>{label}</b>{desc && <span style={{ display: 'block', fontSize: T.sm, color: MUTED, marginTop: 2, lineHeight: 1.6 }}>{desc}</span>}</span>
    </label>
  );
}

/** 저장 대상만 비교 (저장하지 않은 변경 여부) */
const keyOf = (v: LegalSettings | null) => (v ? JSON.stringify({ sms: v.sms, a: v.alertOtherLawAmend, n: v.newsEnabled }) : '');

export default function LegalAlertSettingsTab({ onToast, guard }: Props) {
  const [s, setS] = useState<LegalSettings | null>(null);
  const [savedKey, setSavedKey] = useState('');
  const [prov, setProv] = useState<{ google: boolean; naver: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TestSmsResult | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    getSettings().then(v => { const n = { ...v, alertOtherLawAmend: !!v.alertOtherLawAmend, newsEnabled: v.newsEnabled !== false }; setS(n); setSavedKey(keyOf(n)); }).catch(e => setErr(errMsg(e, '설정을 불러오지 못했습니다')));
    Promise.all([getSummary().catch(() => null), getNewsStatus().catch(() => null)])
      .then(([v, n]) => setProv(v?.newsProviders || n?.providers || null));
  }, []);
  const dirty = !!s && keyOf(s) !== savedKey;
  const save = async (): Promise<boolean> => {
    if (!s) return true;
    setSaving(true); setErr('');
    try {
      const v = await saveSettings({ sms: s.sms, alertOtherLawAmend: s.alertOtherLawAmend, newsEnabled: s.newsEnabled });
      const n = v ? { ...s, ...v, alertOtherLawAmend: v.alertOtherLawAmend ?? s.alertOtherLawAmend, newsEnabled: v.newsEnabled ?? s.newsEnabled } : s;
      setS(n); setSavedKey(keyOf(n));
      onToast('알림 설정을 저장했습니다'); return true;
    } catch (e) { setErr(errMsg(e, '저장하지 못했습니다')); return false; }
    finally { setSaving(false); }
  };
  useGuardReport(guard, dirty, save);
  if (!s) return <div style={{ padding: 20, fontSize: T.sm, color: err ? C.red : MUTED }}>{err || <><Spinner /> 불러오는 중…</>}</div>;

  const setSms = (p: Partial<LegalSettings['sms']>) => setS({ ...s, sms: { ...s.sms, ...p } });
  const runTest = async () => {
    setTesting(true); setTest(null); setErr('');
    try { setTest(await testSms(s.sms.phones)); } catch (e) { setErr(errMsg(e, '테스트 발송에 실패했습니다')); }
    finally { setTesting(false); }
  };
  const pill = (ok: boolean) => ({ fontSize: T.xs, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: ok ? '#ECFDF5' : C.bg, color: ok ? '#059669' : MUTED });

  return (
    <div style={{ maxWidth: 640 }}>
      <Toggle on={s.sms.enabled} onChange={v => setSms({ enabled: v })} label="아침 문자 요약"
        desc="새벽 수집이 끝나면 아침 7~9시에 새 법령 개정·판례·해석·국회 법안·입법예고·국무회의 의결을 주제별 건수로 문자 보내 드립니다. 뉴스는 넣지 않습니다." />
      <div style={{ padding: '12px 0', borderBottom: `1px solid ${C.border}`, opacity: s.sms.enabled ? 1 : 0.6 }}>
        <TermChips label="받는 번호" hint="Enter로 추가" value={s.sms.phones} onChange={v => setSms({ phones: v.map(fmtPhone) })} synonyms={[]} placeholder="010-1234-5678" validate={phoneErr} />
        <label style={labelStyle} htmlFor="lw-min">최소 건수 <span style={{ fontWeight: 400, color: MUTED }}>새 글이 이보다 적으면 보내지 않습니다</span></label>
        <input id="lw-min" type="number" min={1} max={99} value={s.sms.minCount} onChange={e => setSms({ minCount: Math.max(1, Math.min(99, Number(e.target.value) || 1)) })} style={{ ...inputStyle, width: 90 }} />
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="lw-f" disabled={testing || !s.sms.phones.length} onClick={runTest} style={{ ...btn(), opacity: testing || !s.sms.phones.length ? 0.6 : 1 }}>
            {testing ? <><Spinner /> 보내는 중</> : '테스트 발송'}
          </button>
          <span style={{ fontSize: T.sm, color: MUTED }}>위에 적힌 번호로 오늘 요약을 바로 보냅니다 (저장하지 않아도 됩니다)</span>
        </div>
        {test && (
          <div style={{ marginTop: 10, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: '10px 12px', background: C.bgAlt }}>
            <div style={{ fontSize: T.sm, color: MUTED, marginBottom: 4 }}>오늘 새로 들어온 {test.total}건으로 만든 문자입니다 (벨의 「확인 전 전체」 수와 다를 수 있습니다)</div>
            <div style={{ fontSize: T.md, color: C.sub, whiteSpace: 'pre-wrap', marginBottom: 6 }}>{test.message}</div>
            {test.results.map(r => (
              <div key={r.phone} style={{ fontSize: T.sm, color: r.ok ? '#059669' : C.red }}>{r.ok ? '✓ 보냄' : '✕ 못 보냄'} {r.phone}{r.error ? ` — ${smsErr(r.error)}` : ''}</div>
            ))}
          </div>
        )}
      </div>
      <Toggle on={s.alertOtherLawAmend} onChange={v => setS({ ...s, alertOtherLawAmend: v })} label="타법개정도 알림"
        desc="다른 법이 바뀌면서 문구만 따라 바뀐 개정(타법개정)도 새 소식으로 알립니다. 끄면 목록에만 흐리게 쌓입니다." />
      <Toggle on={s.newsEnabled} onChange={v => setS({ ...s, newsEnabled: v })} label="뉴스 수집"
        desc="매일 새벽 4시 일괄 수집 때 주제별 뉴스 검색어로 기사를 모읍니다. 뉴스는 알림 없이 각 주제의 뉴스 탭에 쌓입니다." />
      <div style={{ padding: '10px 0', borderBottom: `1px solid ${C.border}` }}><PcNotifyToggle onToast={onToast} /></div>
      <div style={{ padding: '12px 0' }}>
        <span style={labelStyle}>뉴스 제공처</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={pill(prov ? prov.google : true)}>구글 뉴스 {prov && !prov.google ? '꺼짐' : '연결됨'}</span>
          <span style={pill(!!prov?.naver)}>네이버 뉴스 {prov?.naver ? '연결됨' : '꺼짐'}</span>
        </div>
        {!prov?.naver && <p style={{ fontSize: T.sm, color: MUTED, margin: '6px 0 0' }}>네이버 뉴스는 네이버 클라우드 NAVER API HUB 키를 서버에 넣으면 켜집니다</p>}
      </div>
      {err && <div role="alert" style={{ fontSize: T.sm, color: C.red, margin: '6px 0' }}>{err}</div>}
      <div style={{ position: 'sticky', bottom: 0, background: C.white, borderTop: `1px solid ${C.border}`, padding: '12px 0', display: 'flex', alignItems: 'center', gap: 10, zIndex: 2 }}>
        <button type="button" className="lw-f" disabled={saving || !dirty} onClick={save} style={{ ...btn('pri'), opacity: saving || !dirty ? 0.6 : 1 }}>{saving ? '저장 중…' : '저장'}</button>
        <span style={{ fontSize: T.sm, color: dirty ? '#B45309' : MUTED }}>{dirty ? '저장하지 않은 변경이 있습니다' : '저장됨'}</span>
      </div>
    </div>
  );
}
